import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

/** Anthropic Messages API `image` content block (base64 source). */
export interface ChatImageBlock {
  type: 'image'
  source: {
    type: 'base64'
    media_type: string
    data: string
  }
}

export interface ChatMessage {
  id: number
  chat_id: string
  role: string
  content: string
  created_at: string
  images?: ChatImageBlock[] | null
}

export interface Chat {
  id: string
  app_id: string
  container_id: string | null
  status: string
  created_at: string
  last_message_at: string
  push_branch?: string | null
  pr_url?: string | null
  messages?: ChatMessage[]
}

export interface SendError {
  message: string
  content: string
}

interface ChatState {
  items: Chat[]
  closedTotal: number
  closedLoaded: number
  loadingMore: boolean
  loading: boolean
  error: string | null
  creating: boolean
  sending: Record<string, boolean>
  sendErrors: Record<string, SendError>
}

const initialState: ChatState = {
  items: [],
  closedTotal: 0,
  closedLoaded: 0,
  loadingMore: false,
  loading: false,
  error: null,
  creating: false,
  sending: {},
  sendErrors: {},
}

let chatsFetchInFlight = false

export const fetchChats = createAsyncThunk(
  'chats/fetch',
  async (appId: string) => {
    const response = await fetch(`/api/chat/${appId}`)
    if (!response.ok) throw new Error('Failed to fetch chats')
    return response.json() as Promise<{ chats: Chat[]; closedTotal: number }>
  },
  {
    condition: () => {
      if (chatsFetchInFlight) return false
      chatsFetchInFlight = true
      return true
    },
  },
)

export const fetchMoreClosedChats = createAsyncThunk(
  'chats/fetchMoreClosed',
  async ({ appId, offset }: { appId: string; offset: number }) => {
    const response = await fetch(`/api/chat/${appId}?closedOffset=${offset}&closedLimit=20`)
    if (!response.ok) throw new Error('Failed to fetch more closed chats')
    const data = await response.json() as { chats: Chat[]; closedTotal: number }
    const closed = data.chats.filter((c) => c.status !== 'active')
    return { closed, closedTotal: data.closedTotal }
  },
)

// Per-chat in-flight guard. Without it the ChatList effect re-dispatches a
// detail fetch for every still-pending chat each time `items` changes, fanning
// out to O(N²) requests (see docs/bugs/LoginLoop — the request burst that
// tripped Auth0's rate limit). Mirrors the module-level chatsFetchInFlight guard.
const chatDetailInFlight = new Set<string>()

export const fetchChatDetail = createAsyncThunk(
  'chats/fetchDetail',
  async ({ appId, chatId }: { appId: string; chatId: string }) => {
    const response = await fetch(`/api/chat/${appId}/${chatId}`)
    if (!response.ok) throw new Error('Failed to fetch chat detail')
    return response.json() as Promise<Chat & { messages: ChatMessage[] }>
  },
  {
    // Drop the dispatch in the thunk layer so it covers the mount effect, the
    // 5s poll, and the interrupt path uniformly.
    condition: ({ chatId }) => {
      if (chatDetailInFlight.has(chatId)) return false
      chatDetailInFlight.add(chatId)
      return true
    },
  },
)

export const createChat = createAsyncThunk('chats/create', async (arg: string | { appId: string; branch?: string; agent?: string }) => {
  const { appId, branch, agent } = typeof arg === 'string' ? { appId: arg, branch: undefined, agent: undefined } : arg
  const body: { branch?: string; agent?: string } = {}
  if (branch) body.branch = branch
  if (agent) body.agent = agent
  const response = await fetch(`/api/chat/${appId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) throw new Error('Failed to create chat')
  return response.json() as Promise<Chat>
})

export const sendMessage = createAsyncThunk(
  'chats/sendMessage',
  async ({ appId, chatId, content, images }: { appId: string; chatId: string; content: string; images?: ChatImageBlock[] }, { rejectWithValue }) => {
    const response = await fetch(`/api/chat/${appId}/${chatId}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, ...(images && images.length > 0 ? { images } : {}) }),
    })
    if (!response.ok) {
      let message = 'Failed to send message'
      try {
        const data = await response.json() as { error?: string }
        if (data.error) message = data.error
      } catch { /* use default */ }
      if (response.status === 401) message = 'You must be signed in to send messages'
      return rejectWithValue({ chatId, content, message })
    }
    const data = await response.json() as { userMessage: ChatMessage }
    return { chatId, userMessage: data.userMessage }
  },
)

const chatSlice = createSlice({
  name: 'chats',
  initialState,
  reducers: {
    clearChats() {
      return initialState
    },
    clearSendError(state, action: { payload: string }) {
      delete state.sendErrors[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChats.pending, (state) => {
        if (state.items.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchChats.fulfilled, (state, action) => {
        chatsFetchInFlight = false
        state.loading = false
        const { chats, closedTotal } = action.payload
        state.closedTotal = closedTotal
        // Preserve messages from previously fetched detail
        const existingMessages = new Map<string, ChatMessage[]>()
        for (const item of state.items) {
          if (item.messages) existingMessages.set(item.id, item.messages)
        }
        const newIds = new Set(chats.map((c) => c.id))
        // Keep any previously-loaded closed chats not in the new response (from "show more")
        const extraClosed = state.items.filter((c) => c.status !== 'active' && !newIds.has(c.id))
        const merged = chats.map((chat) => ({
          ...chat,
          messages: existingMessages.get(chat.id) || chat.messages,
        }))
        for (const c of extraClosed) {
          merged.push({ ...c, messages: existingMessages.get(c.id) || c.messages })
        }
        state.items = merged
        state.closedLoaded = merged.filter((c) => c.status !== 'active').length
      })
      .addCase(fetchChats.rejected, (state, action) => {
        chatsFetchInFlight = false
        state.loading = false
        state.error = action.error.message || 'Failed to fetch chats'
      })
      .addCase(fetchChatDetail.fulfilled, (state, action) => {
        chatDetailInFlight.delete(action.payload.id)
        const idx = state.items.findIndex((c) => c.id === action.payload.id)
        if (idx >= 0) {
          state.items[idx] = action.payload
        } else {
          state.items.unshift(action.payload)
        }
      })
      .addCase(fetchChatDetail.rejected, (_state, action) => {
        // Only release the guard when the request actually ran. A dispatch that
        // was dropped by the in-flight condition also lands here (meta.condition)
        // but must NOT clear the flag the running request owns. Without a
        // rejected case at all, a failed detail fetch would leave `messages`
        // undefined forever and the effect would retry it on every items change.
        if (action.meta.condition) return
        chatDetailInFlight.delete(action.meta.arg.chatId)
      })
      .addCase(createChat.pending, (state) => {
        state.creating = true
      })
      .addCase(createChat.fulfilled, (state, action) => {
        state.creating = false
        state.items.unshift({ ...action.payload, messages: [] })
      })
      .addCase(createChat.rejected, (state) => {
        state.creating = false
      })
      .addCase(sendMessage.pending, (state, action) => {
        state.sending[action.meta.arg.chatId] = true
        delete state.sendErrors[action.meta.arg.chatId]
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { chatId, userMessage } = action.payload
        state.sending[chatId] = false
        const chat = state.items.find((c) => c.id === chatId)
        if (chat) {
          if (!chat.messages) chat.messages = []
          if (!chat.messages.some((m) => m.id === userMessage.id)) {
            chat.messages.push(userMessage)
          }
          chat.last_message_at = userMessage.created_at
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        const chatId = action.meta.arg.chatId
        state.sending[chatId] = false
        const payload = action.payload as { chatId: string; content: string; message: string } | undefined
        if (payload) {
          state.sendErrors[chatId] = { message: payload.message, content: payload.content }
        } else {
          state.sendErrors[chatId] = { message: 'Failed to send message', content: action.meta.arg.content }
        }
      })
      .addCase(fetchMoreClosedChats.pending, (state) => {
        state.loadingMore = true
      })
      .addCase(fetchMoreClosedChats.fulfilled, (state, action) => {
        state.loadingMore = false
        const { closed, closedTotal } = action.payload
        state.closedTotal = closedTotal
        const existingIds = new Set(state.items.map((c) => c.id))
        for (const chat of closed) {
          if (!existingIds.has(chat.id)) {
            state.items.push(chat)
          }
        }
        state.closedLoaded = state.items.filter((c) => c.status !== 'active').length
      })
      .addCase(fetchMoreClosedChats.rejected, (state) => {
        state.loadingMore = false
      })
  },
})

export const { clearChats, clearSendError } = chatSlice.actions
export type { ChatState }
export default chatSlice.reducer
